/**
 * 用户消费日志页面
 * 用户可以查看自己的充值和消费记录
 */

import { LayoutWithSidebar } from "@/components/LayoutWithSidebar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { LogEntry, LogQueryParams } from "@/services/userLogsApi";
import { getUserLogStats, getUserLogs } from "@/services/userLogsApi";
import { useAuthStore } from "@/stores/auth";
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Coins,
  FileText,
  Filter,
  RefreshCw,
  Search,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * 日志类型配置
 */
const LOG_TYPES = [
  { value: "0", label: "全部类型" },
  { value: "1", label: "充值" },
  { value: "2", label: "消费" },
  { value: "3", label: "管理" },
  { value: "4", label: "系统" },
];

/**
 * 获取日志类型的 Badge 样式
 */
function getLogTypeBadge(type: number, typeName: string) {
  switch (type) {
    case 1:
      return (
        <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">
          <ArrowUp className="h-3 w-3 mr-1" />
          {typeName}
        </Badge>
      );
    case 2:
      return (
        <Badge className="bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-100">
          <ArrowDown className="h-3 w-3 mr-1" />
          {typeName}
        </Badge>
      );
    case 3:
      return (
        <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100">
          {typeName}
        </Badge>
      );
    case 4:
      return (
        <Badge className="bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100">
          {typeName}
        </Badge>
      );
    default:
      return <Badge variant="outline">{typeName}</Badge>;
  }
}

/**
 * 格式化时间显示
 */
function formatDateTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * 格式化 Token 数量
 */
function formatQuota(quota: number): string {
  if (quota === 0) return "0";
  return quota.toLocaleString();
}

export function UserLogsPage() {
  const { user } = useAuthStore();

  // 日志数据
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // 统计数据
  const [totalQuota, setTotalQuota] = useState<number | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // 筛选条件
  const [logType, setLogType] = useState("0");
  const [modelName, setModelName] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // 获取查询参数
  const getQueryParams = useCallback(
    (pageNum: number): LogQueryParams => {
      const params: LogQueryParams = { page: pageNum };
      const typeNum = Number(logType);
      if (typeNum > 0) {
        params.type = typeNum;
      }
      if (modelName.trim()) {
        params.modelName = modelName.trim();
      }
      return params;
    },
    [logType, modelName]
  );

  // 加载日志数据
  const loadLogs = useCallback(
    async (pageNum: number, append = false) => {
      try {
        setLoading(true);
        const params = getQueryParams(pageNum);
        const result = await getUserLogs(params);

        if (result.success) {
          const newLogs = result.data.logs;
          if (append) {
            setLogs((prev) => [...prev, ...newLogs]);
          } else {
            setLogs(newLogs);
          }
          // 如果返回数量少于一页（假设每页14条），说明没有更多了
          setHasMore(newLogs.length >= 14);
          setPage(pageNum);
        } else {
          toast.error(result.message || "获取日志失败");
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "获取日志失败");
      } finally {
        setLoading(false);
      }
    },
    [getQueryParams]
  );

  // 加载统计数据
  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const params: LogQueryParams = {};
      const typeNum = Number(logType);
      if (typeNum > 0) {
        params.type = typeNum;
      }
      if (modelName.trim()) {
        params.modelName = modelName.trim();
      }
      const result = await getUserLogStats(params);

      if (result.success) {
        setTotalQuota(result.data.totalQuota);
      }
    } catch (_error) {
      // 统计加载失败不影响主要功能
    } finally {
      setStatsLoading(false);
    }
  }, [logType, modelName]);

  // 搜索/刷新
  const handleSearch = useCallback(() => {
    setPage(0);
    loadLogs(0);
    loadStats();
  }, [loadLogs, loadStats]);

  // 加载下一页
  const handleNextPage = useCallback(() => {
    loadLogs(page + 1);
  }, [loadLogs, page]);

  // 加载上一页
  const handlePrevPage = useCallback(() => {
    if (page > 0) {
      loadLogs(page - 1);
    }
  }, [loadLogs, page]);

  // 初始加载（仅执行一次）
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      loadLogs(0);
      loadStats();
    }
  }, [loadLogs, loadStats]);

  if (!user) {
    return (
      <LayoutWithSidebar>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-gray-500">请先登录</p>
        </div>
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar>
      <div className="container mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* 标题 */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">消费日志</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            查看您的充值和消费记录
          </p>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                总消耗额度
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Coins className="h-5 w-5 text-orange-500" />
                <div className="text-xl sm:text-2xl font-bold text-orange-600">
                  {statsLoading ? "..." : formatQuota(totalQuota ?? 0)}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                累计消耗 Token
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                当前页记录
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-500" />
                <div className="text-xl sm:text-2xl font-bold text-blue-600">
                  {loading ? "..." : logs.length}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                第 {page + 1} 页
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                筛选类型
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-gray-600">
                {LOG_TYPES.find((t) => t.value === logType)?.label || "全部"}
              </div>
              <p className="text-xs text-muted-foreground mt-1">当前筛选条件</p>
            </CardContent>
          </Card>
        </div>

        {/* 筛选和操作栏 */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                  <Search className="h-4 w-4 sm:h-5 sm:w-5" />
                  日志查询
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  按类型和模型筛选日志记录
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="h-4 w-4 mr-1" />
                  筛选
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`}
                  />
                  刷新
                </Button>
              </div>
            </div>
          </CardHeader>

          {/* 筛选区域 */}
          {showFilters && (
            <CardContent className="pt-0 pb-3 border-b">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <Select value={logType} onValueChange={setLogType}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="选择日志类型" />
                    </SelectTrigger>
                    <SelectContent>
                      {LOG_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Input
                    placeholder="按模型名称筛选"
                    value={modelName}
                    onChange={(e) => setModelName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearch();
                      }
                    }}
                  />
                </div>
                <Button onClick={handleSearch} disabled={loading}>
                  <Search className="h-4 w-4 mr-1" />
                  查询
                </Button>
              </div>
            </CardContent>
          )}

          {/* 日志表格 */}
          <CardContent className={showFilters ? "pt-4" : ""}>
            {loading && logs.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">加载中...</span>
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mb-3 opacity-50" />
                <p className="text-base font-medium">暂无日志记录</p>
                <p className="text-sm mt-1">使用 API 或充值后将显示相关记录</p>
              </div>
            ) : (
              <>
                {/* 桌面端表格 */}
                <div className="hidden sm:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[80px]">类型</TableHead>
                        <TableHead>描述</TableHead>
                        <TableHead>模型</TableHead>
                        <TableHead className="text-right">Token 消耗</TableHead>
                        <TableHead className="text-right">提示词</TableHead>
                        <TableHead className="text-right">补全</TableHead>
                        <TableHead className="w-[170px]">时间</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log, index) => (
                        <TableRow key={`${log.createdTimestamp}-${index}`}>
                          <TableCell>
                            {getLogTypeBadge(log.type, log.typeName)}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {log.content || "-"}
                          </TableCell>
                          <TableCell>
                            {log.modelName ? (
                              <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                                {log.modelName}
                              </code>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {log.type === 1 ? (
                              <span className="text-green-600">
                                +{formatQuota(log.quota)}
                              </span>
                            ) : (
                              <span className="text-orange-600">
                                {formatQuota(log.quota)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm text-muted-foreground">
                            {log.promptTokens > 0
                              ? formatQuota(log.promptTokens)
                              : "-"}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm text-muted-foreground">
                            {log.completionTokens > 0
                              ? formatQuota(log.completionTokens)
                              : "-"}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDateTime(log.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* 移动端卡片列表 */}
                <div className="sm:hidden space-y-3">
                  {logs.map((log, index) => (
                    <div
                      key={`mobile-${log.createdTimestamp}-${index}`}
                      className="p-3 border rounded-lg space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        {getLogTypeBadge(log.type, log.typeName)}
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(log.createdAt)}
                        </span>
                      </div>
                      {log.content && (
                        <p className="text-sm text-gray-700 truncate">
                          {log.content}
                        </p>
                      )}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {log.modelName ? (
                            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                              {log.modelName}
                            </code>
                          ) : (
                            "无模型"
                          )}
                        </span>
                        <span className="font-mono font-medium">
                          {log.type === 1 ? (
                            <span className="text-green-600">
                              +{formatQuota(log.quota)}
                            </span>
                          ) : (
                            <span className="text-orange-600">
                              {formatQuota(log.quota)}
                            </span>
                          )}
                        </span>
                      </div>
                      {(log.promptTokens > 0 || log.completionTokens > 0) && (
                        <div className="flex gap-4 text-xs text-muted-foreground">
                          <span>提示词: {formatQuota(log.promptTokens)}</span>
                          <span>补全: {formatQuota(log.completionTokens)}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* 分页控制 */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={page === 0 || loading}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    上一页
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    第 {page + 1} 页
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={!hasMore || loading}
                  >
                    下一页
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </LayoutWithSidebar>
  );
}
